import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { Notice } from '@/modules/supply/components/LoadEstimateParts';
import { LoadEstimatePendingOrders } from '@/modules/supply/components/LoadEstimatePendingOrders';
import { LoadEstimateSuppliersTable } from '@/modules/supply/components/LoadEstimateSuppliersTable';
import { LoadEstimateSummary } from '@/modules/supply/components/LoadEstimateSummary';
import { LoadEstimateTruck } from '@/modules/supply/components/LoadEstimateTruck';
import { useLoadEstimate } from '@/modules/supply/hooks/useLoadEstimate';
import { useLoadEstimateTruck } from '@/modules/supply/hooks/useLoadEstimateTruck';
import { LOAD_ESTIMATE_KEY as T } from '@/modules/supply/utils/loadEstimateDraft';

/** El estado del módulo es de un almacén: al cambiar de almacén se parte de cero. */
export function LoadEstimatePage() {
  const warehouseId = useWarehouseId();
  return <LoadEstimateContent key={String(warehouseId ?? 'none')} />;
}

function LoadEstimateContent() {
  const { t } = useTranslation();
  const estimate = useLoadEstimate();
  const truck = useLoadEstimateTruck(estimate);
  const {
    warehouseId, heldDraft, setHeldDraft, candidates, restoreDraft, discardHeldDraft, restoreNotice, setRestoreNotice, error, created,
  } = estimate;

  if (typeof warehouseId !== 'number') return <p className="text-sm text-gray-600">{t(`${T}.noWarehouse`)}</p>;

  return (
    <div className="space-y-6">
      {heldDraft ? (
        <Notice tone="info">
          <span>
            {t(`${T}.heldDraftNotice`, {
              suppliers: [...new Set(heldDraft.lines.map((line) => line.supplier_id))]
                .map((id) => candidates.find((row) => row.supplier_id === id)?.supplier_name ?? `#${id}`)
                .join(', '),
            })}
          </span>
          <span className="flex items-center gap-4">
            <button type="button" onClick={() => { const draft = heldDraft; setHeldDraft(null); restoreDraft(draft); }} className="font-semibold underline">{t(`${T}.heldDraftResume`)}</button>
            <button type="button" onClick={() => void discardHeldDraft()} className="font-medium opacity-70 hover:underline">{t(`${T}.discard`)}</button>
          </span>
        </Notice>
      ) : null}
      {restoreNotice ? (
        <Notice tone="warning">
          <span>{restoreNotice}</span>
          <button type="button" onClick={() => setRestoreNotice(null)} className="font-semibold underline">{t(`${T}.dismiss`)}</button>
        </Notice>
      ) : null}
      {error ? <Notice tone="error" role="alert"><span>{error}</span></Notice> : null}
      {created ? (
        <Notice tone="success">
          <span>{created}</span>
          <Link to="/supply" className="font-semibold underline">{t(`${T}.goToSupply`)}</Link>
        </Notice>
      ) : null}

      <LoadEstimateSuppliersTable estimate={estimate} activeSourceId={truck.activeSource?.supplierId ?? null} />
      <LoadEstimateSummary estimate={estimate} truck={truck} />
      <LoadEstimateTruck estimate={estimate} truck={truck} />

      <LoadEstimatePendingOrders warehouseId={warehouseId} />
    </div>
  );
}
